/**
 * Component registry singleton.
 *
 * Stores `ComponentRegistration` entries keyed by `id` and exposes the
 * minimal lookup API used by the admin config page, the CMD/Ctrl+click
 * handler, and the `withComponentRules` HOC (Phase 6).
 *
 * The registry is a module-level `Map`, intentionally similar in shape to
 * `dataListWorkflowRegistry` — self-contained, no React imports, and
 * populated at module bootstrap via `register<module>()` calls.
 *
 * Usage:
 * ```ts
 * import { registerComponent, getComponent } from '@components/shared/configurable';
 *
 * registerComponent({ id: "claimflow.openCoverageCard", ... });
 *
 * const reg = getComponent("claimflow.openCoverageCard");
 * ```
 */

import type { ComponentRegistration } from './types';

// ---------------------------------------------------------------------------
// Internal storage
// ---------------------------------------------------------------------------

/**
 * Module-level component map. `ComponentRegistration<unknown>` keeps the
 * storage type homogeneous while individual registrations stay strongly
 * typed at their call site via the generic `registerComponent<P>()`.
 */
const componentRegistry = new Map<string, ComponentRegistration<unknown>>();

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Register a component with the configurable registry.
 *
 * Throws if a component with the same `id` has already been registered.
 * Registration should happen once per component at module bootstrap
 * (see `src/modules/<module>/components/register.ts`). Module
 * registrars are responsible for making the bootstrap idempotent — they
 * can either short-circuit on a module-level flag or guard each call
 * with `isComponentRegistered(id)`.
 *
 * @param registration - Metadata describing the component.
 * @throws If `registration.id` is already present in the registry.
 */
export function registerComponent<P>(
  registration: ComponentRegistration<P>,
): void {
  if (componentRegistry.has(registration.id)) {
    throw new Error(
      `Component "${registration.id}" is already registered. ` +
        'Each component id must be unique across the configurable registry. ' +
        'Guard your module bootstrap with isComponentRegistered() or a ' +
        'one-shot flag before calling registerComponent().',
    );
  }

  // `ComponentRegistration<P>` widens to `ComponentRegistration<unknown>`
  // for storage; the runtime shape is identical and callers retrieve the
  // record as the same wide type.
  componentRegistry.set(
    registration.id,
    registration as unknown as ComponentRegistration<unknown>,
  );
}

/**
 * Look up a component registration by id.
 *
 * @param id - The registration id (e.g., `"claimflow.openCoverageCard"`).
 * @returns The registration, or `undefined` if not registered.
 */
export function getComponent(
  id: string,
): ComponentRegistration<unknown> | undefined {
  return componentRegistry.get(id);
}

/**
 * Return every registered component, preserving insertion order.
 *
 * Useful for admin list pages that show every configurable component
 * across modules.
 */
export function getAllComponents(): ComponentRegistration<unknown>[] {
  return Array.from(componentRegistry.values());
}

/**
 * Return every component belonging to a given module.
 *
 * Matches on the `module` field (not the `id` prefix) to avoid
 * ambiguity when modules pick ids outside the `<module>.<name>`
 * convention.
 *
 * @param moduleId - The module id (e.g., `"claimflow"`).
 */
export function getComponentsByModule(
  moduleId: string,
): ComponentRegistration<unknown>[] {
  return Array.from(componentRegistry.values()).filter(
    (reg) => reg.module === moduleId,
  );
}

/**
 * Check whether a component with the given id is already registered.
 *
 * Intended for idempotent bootstrap code — module registrars can use
 * this to safely call `registerComponent()` multiple times (e.g., in
 * test re-imports) without hitting the double-registration guard.
 */
export function isComponentRegistered(id: string): boolean {
  return componentRegistry.has(id);
}

/**
 * Clear the registry.
 *
 * **Test-only utility.** Production code must never call this — wiping
 * the registry mid-session would break any component currently depending
 * on its registration. Tests call it from `beforeEach` to isolate
 * registry state between cases.
 */
export function resetRegistry(): void {
  componentRegistry.clear();
}
